import React, { useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Search, ArrowUpDown, Filter, BarChart3, Loader2 } from 'lucide-react';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  useGetKvsResult9n11RegionwiseQuery,
  useGetKvsResult9n11RoDetailsQuery,
  useGetKvsResult9n11SchoolDetailQuery,
} from '../../../services/kvsResult9n11Api';
import { CLASS_NAMES } from '../data/index';
import type { LevelMetrics } from '../../../types';
import Pagination from '../../../../../shared/components/Pagination';

const BASE = '/kvs-samagam/student-analytics/stages/secondary';

const LEVEL_SLUGS: Record<string, string> = {
  excellent: 'Excellent',
  good: 'Good',
  average: 'Average',
  'below-average': 'Below Average',
};

function getCompetencyLevel(avg: number): string {
  if (avg >= 85) return 'Excellent';
  if (avg >= 70) return 'Good';
  if (avg >= 55) return 'Average';
  return 'Below Average';
}

function levelColor(level: string): string {
  switch (level) {
    case 'Excellent': return 'bg-emerald-50 text-emerald-700';
    case 'Good': return 'bg-indigo-50 text-indigo-700';
    case 'Average': return 'bg-amber-50 text-amber-700';
    default: return 'bg-red-50 text-red-700';
  }
}

interface EntityRow {
  id: string;
  name: string;
  subtitle: string;
  metrics: LevelMetrics;
  level: string;
  navigateTo: string;
}

type SortKey = 'name' | 'students' | 'competency' | 'needsSupport' | 'excellent';

interface EntityListScreenProps {
  entityType: 'regions' | 'schools' | 'classes';
}

const EntityListScreen: React.FC<EntityListScreenProps> = ({ entityType }) => {
  const { regionId, schoolId, level } = useParams<{ regionId?: string; schoolId?: string; level?: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const filterLevel = level ? LEVEL_SLUGS[level] : undefined;
  const academicYear = Number(searchParams.get('year') || '2025');
  // Class 9/11 results carry no term dimension — always query "overall" (0).
  const term = 0;
  const querySuffix = `?year=${academicYear}`;

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('competency');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 15;

  // ========== API Queries ==========
  const { data: regionRes, isLoading: regionLoading } = useGetKvsResult9n11RegionwiseQuery(
    entityType === 'regions' ? { year: academicYear, term } : skipToken
  );

  const { data: roDetailsRes, isLoading: roLoading } = useGetKvsResult9n11RoDetailsQuery(
    entityType === 'schools' && regionId ? { year: academicYear, term, roid: Number(regionId) } : skipToken
  );

  const { data: schoolDetailRes, isLoading: schoolLoading } = useGetKvsResult9n11SchoolDetailQuery(
    entityType === 'classes' && schoolId ? { year: academicYear, term, kvid: Number(schoolId) } : skipToken
  );

  const isLoading = regionLoading || roLoading || schoolLoading;

  const allEntities: EntityRow[] = useMemo(() => {
    if (entityType === 'regions') {
      const regions = regionRes?.data?.regions || [];
      return regions.map(r => ({
        id: String(r.roID),
        name: r.roName,
        subtitle: `RO ID: ${r.roID}`,
        metrics: {
          totalStudents: r.totalStudents,
          avgAttendance: 0,
          avgCompetency: r.avgMarksPercentage,
          needsSupportCount: 0,
          excellentCount: 0,
          avgBehaviorScore: 0,
          avgPeerScore: 0,
          bmiNormalPercent: 0,
        },
        level: getCompetencyLevel(r.avgMarksPercentage),
        navigateTo: `${BASE}/region/${r.roID}${querySuffix}`,
      }));
    }
    if (entityType === 'schools') {
      const schools = roDetailsRes?.data?.schools || [];
      return schools.map(s => ({
        id: String(s.schoolID),
        name: s.schoolName,
        subtitle: `School ID: ${s.schoolID}`,
        metrics: {
          totalStudents: s.totalStudents,
          avgAttendance: 0,
          avgCompetency: s.avgMarks,
          needsSupportCount: s.needSupport,
          excellentCount: s.excellentStudents,
          avgBehaviorScore: 0,
          avgPeerScore: 0,
          bmiNormalPercent: 0,
        },
        level: getCompetencyLevel(s.avgMarks),
        navigateTo: `${BASE}/region/${regionId}/school/${s.schoolID}${querySuffix}`,
      }));
    }
    const byClass = schoolDetailRes?.data?.byClass || {};
    return Object.entries(byClass).map(([classKey, classData]) => ({
      id: classKey,
      name: CLASS_NAMES[classKey] || `Class ${classKey}`,
      subtitle: `Section A`,
      metrics: {
        totalStudents: classData.totalStudents,
        avgAttendance: 0,
        avgCompetency: classData.avgMarks,
        needsSupportCount: classData.needSupport,
        excellentCount: classData.excellentStudents,
        avgBehaviorScore: 0,
        avgPeerScore: 0,
        bmiNormalPercent: 0,
      },
      level: getCompetencyLevel(classData.avgMarks),
      navigateTo: `${BASE}/region/${regionId}/school/${schoolId}/class/${classKey}${querySuffix}`,
    }));
  }, [entityType, querySuffix, regionId, schoolId, regionRes, roDetailsRes, schoolDetailRes]);

  const filteredEntities = useMemo(() => {
    let list = [...allEntities];
    if (filterLevel) list = list.filter(e => e.level === filterLevel);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(e => e.name.toLowerCase().includes(q) || e.subtitle.toLowerCase().includes(q));
    }
    list.sort((a, b) => {
      switch (sortKey) {
        case 'name': return sortAsc ? a.name.localeCompare(b.name) : b.name.localeCompare(a.name);
        case 'students': return sortAsc ? a.metrics.totalStudents - b.metrics.totalStudents : b.metrics.totalStudents - a.metrics.totalStudents;
        case 'competency': return sortAsc ? a.metrics.avgCompetency - b.metrics.avgCompetency : b.metrics.avgCompetency - a.metrics.avgCompetency;
        case 'needsSupport': return sortAsc ? a.metrics.needsSupportCount - b.metrics.needsSupportCount : b.metrics.needsSupportCount - a.metrics.needsSupportCount;
        case 'excellent': return sortAsc ? a.metrics.excellentCount - b.metrics.excellentCount : b.metrics.excellentCount - a.metrics.excellentCount;
        default: return 0;
      }
    });
    return list;
  }, [allEntities, filterLevel, search, sortKey, sortAsc]);

  const totalPages = Math.ceil(filteredEntities.length / pageSize);
  const pageEntities = filteredEntities.slice((page - 1) * pageSize, page * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(false); }
  };

  const entityLabel = entityType === 'regions' ? 'Regions' : entityType === 'schools' ? 'Schools' : 'Classes';
  let parentLabel = '';
  if (entityType === 'schools') {
    parentLabel = roDetailsRes?.data?.roName ? ` — ${roDetailsRes.data.roName} Region` : '';
  } else if (entityType === 'classes') {
    parentLabel = schoolDetailRes?.data?.schoolName ? ` — ${schoolDetailRes.data.schoolName}` : '';
  }

  const title = filterLevel ? `${filterLevel} ${entityLabel}` : `All ${entityLabel}`;

  const buildFilterPath = (lvl: string, active: boolean) => {
    const slug = lvl.toLowerCase().replace(/\s+/g, '-');
    if (entityType === 'regions') return `${BASE}/list/regions${active ? '' : `/${slug}`}${querySuffix}`;
    if (entityType === 'schools') return `${BASE}/region/${regionId}/list/schools${active ? '' : `/${slug}`}${querySuffix}`;
    return `${BASE}/region/${regionId}/school/${schoolId}/list/classes${active ? '' : `/${slug}`}${querySuffix}`;
  };

  const SortHeader: React.FC<{ label: string; k: SortKey }> = ({ label, k }) => (
    <th
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase cursor-pointer hover:text-indigo-600 select-none"
      onClick={() => toggleSort(k)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <ArrowUpDown size={12} className={sortKey === k ? 'text-indigo-600' : 'text-gray-300'} />
      </span>
    </th>
  );

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">
            {filteredEntities.length} {entityLabel.toLowerCase()}{parentLabel}
            {filterLevel && (
              <span className={`ml-2 text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor(filterLevel)}`}>
                Filtered: {filterLevel}
              </span>
            )}
          </p>
        </div>
        {entityType === 'regions' && (
          <button
            onClick={() => navigate(`${BASE}/school-performance${querySuffix}`)}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 active:bg-indigo-800 text-white text-sm font-semibold px-4 py-2 transition-colors shadow-sm"
          >
            <BarChart3 size={16} />
            School Performance
          </button>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-400" />
          {['Excellent', 'Good', 'Average', 'Below Average'].map(lvl => {
            const isActive = filterLevel === lvl;
            return (
              <button
                key={lvl}
                onClick={() => navigate(buildFilterPath(lvl, isActive))}
                className={`text-xs font-medium px-3 py-1.5 rounded-full border transition ${
                  isActive
                    ? `${levelColor(lvl)} border-current`
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
              >
                {lvl} ({allEntities.filter(e => e.level === lvl).length})
              </button>
            );
          })}
        </div>
        <div className="relative">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={e => { setSearch(e.target.value); setPage(1); }}
                placeholder={`Search ${entityLabel.toLowerCase()}...`}
                className="pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 w-56"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="overflow-x-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-400 mr-2" /> Loading list...
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <SortHeader label="Name" k="name" />
                  <SortHeader label="Students" k="students" />
                  <SortHeader label="Competency" k="competency" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Level</th>
                  <SortHeader label="Need Support" k="needsSupport" />
                  <SortHeader label="Excellent" k="excellent" />
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Behavior</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">BMI Normal</th>
                </tr>
              </thead>
              <tbody>
                {pageEntities.map(e => (
                  <tr
                    key={e.id}
                    onClick={() => navigate(e.navigateTo)}
                    className="border-b border-gray-100 hover:bg-indigo-50/40 cursor-pointer transition"
                  >
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{e.name}</p>
                      <p className="text-[11px] text-gray-400">{e.subtitle}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold text-gray-900">{e.metrics.totalStudents.toLocaleString()}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold ${e.metrics.avgCompetency >= 85 ? 'text-emerald-600' : e.metrics.avgCompetency >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                        {e.metrics.avgCompetency}%
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${levelColor(e.level)}`}>{e.level}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-semibold ${e.metrics.needsSupportCount === 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {e.metrics.needsSupportCount.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm font-semibold text-emerald-600">{e.metrics.excellentCount.toLocaleString()}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">{e.metrics.avgBehaviorScore}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{e.metrics.bmiNormalPercent}%</td>
                  </tr>
                ))}
                {pageEntities.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-400">
                      No {entityLabel.toLowerCase()} found{filterLevel ? ` with level "${filterLevel}"` : ''}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {totalPages > 1 && (
          <div className="px-5 py-3 border-t border-gray-100">
            <Pagination
              currentPage={page}
              totalPages={totalPages}
              totalRecords={filteredEntities.length}
              pageSize={pageSize}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default EntityListScreen;
